import type { LocaleContent } from "./types";
import { WEBSITE_BASE } from "../lib/siteBase";

export const en: LocaleContent = {
  lang: "en",
  localePath: `${WEBSITE_BASE}/en/`,
  altLocalePath: `${WEBSITE_BASE}/`,
  nav: {
    home: "Home",
    campsite: "Our Campsite",
    gallery: "Gallery",
    hours: "Opening Hours",
    prices: "Prices",
    directions: "Directions",
    booking: "Online Booking",
  },
  hero: {
    title: "Campingplatz Mainkur",
    subtitle:
      "Pitches on a meadow in Maintal, with a view of the Frankfurt skyline. The Borgstedt family welcomes you from 1 April to 30 September.",
    cta: "Book a pitch",
    welcome: "Borgstedt family · Maintal",
    explore: "How to find us",
    scrollHint: "Read on",
  },
  photoJourney: {
    moments: [
      {
        image: "/images/IMG_4482-scaled.jpg",
        alt: "Evening atmosphere at Campingplatz Mainkur",
        eyebrow: "Arrive",
        title: "This is where your break begins",
        text: "When the sun sinks behind the trees and the campsite glows in warm light – that's when you know: you're in the right place.",
      },
      {
        image: "/images/IMG_0920-scaled.jpg",
        alt: "Families at the campsite",
        eyebrow: "Together",
        title: "Memories that last",
        text: "Children playing on the playground. Parents finally breathing out. Neighbours inviting you for a chat. That's what Mainkur feels like.",
      },
      {
        image: "/images/IMG_0888.jpg",
        alt: "Nature and tranquility at the campsite",
        eyebrow: "Nature",
        title: "Open meadows, old trees, deep calm",
        text: "No noise, no stress – just rustling leaves and views of green. A place where time slows down.",
      },
    ],
  },
  floatingCta: {
    text: "Ready for your place in nature?",
    cta: "Book now",
  },
  news: [
    {
      title: "Season break – we're hibernating ❄️",
      body: `Our season has ended and the campsite has closed its gates. Now we're taking a short break to recharge our batteries.

From October 29, 2025, we'll be available again to answer your inquiries and bookings. During this time, we're preparing the site for the next season.

Please note: The campsite reopens on April 1, 2026. We look forward to welcoming you back in spring!`,
      signature: "Your family Heike and Frank Borgstedt",
    },
    {
      title: "Only arrive with confirmed reservation",
      body: `Dear guests, due to high demand, we kindly ask you to only arrive with a confirmed reservation.

This also applies to cyclists – you are warmly welcome if you arrive during the specified opening hours. Please note that we do not have a separate tent meadow, so if all pitches are occupied, we will not be able to accommodate any more guests.`,
      signature: "Family Heike and Frank Borgstedt",
    },
    {
      title: "Delayed opening due to weather",
      body: `Dear camping guests, we are delighted that you are interested in our campsite.

Unfortunately, due to the wet weather conditions, we will not be able to open until April 8, 2024. We hope for your understanding and look forward to welcoming you.

For reservations, please use our online booking.`,
      signature: "Heike and Frank Borgstedt",
    },
  ],
  quickLinks: {
    title: "Quick Links",
    links: [
      { label: "Site map", href: "#platzplan", icon: "map" },
      { label: "Online booking", href: "BOOKING", icon: "calendar" },
      { label: "Gallery", href: "#galerie", icon: "image" },
      { label: "Our campsite", href: "#unser-campingplatz", icon: "tent" },
    ],
  },
  sitePlan: {
    eyebrow: "Orientation",
    title: "Site map",
    subtitle: "Pitches, sanitary facilities and service points.",
    hint: "Tap or hover over an area",
    legendTitle: "Area",
    compassNorth: "N",
    skylineHint: "Frankfurt skyline",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    resetView: "Reset view",
  },
  facilities: {
    eyebrow: "Facilities",
    title: "What you will find on site",
    subtitle: "Grounds, sanitary block, lounge, kiosk and seasonal pitches.",
    learnMore: "Learn more",
    learnLess: "Show less",
    sitePlanLabel: "Site map",
    tabs: [
      {
        id: "gelaende",
        label: "Camping Area",
        heading: "Camping Area",
        summary: "Park-like meadow with skyline views, playground and table tennis.",
        highlights: ["Skyline views", "Playground & table tennis", "Fully fenced"],
        image: "/images/IMG_0930.jpg",
        sections: [
          {
            title: "Camping Area",
            text: `Our pitches are located on a park-like and even meadow with some old trees that provide pleasant shade. Enjoy the view of the skyline of Frankfurt.`,
          },
          {
            title: "Activities",
            text: `For children we offer a playground with slide, see-saw, sandpit, swing and see-saw animal. There is also a table tennis table for sports enthusiasts. Equipment can be lent at the reception for free.`,
          },
          {
            title: "Safety",
            text: `The campsite is fenced and not accessible to unauthorized persons. Each guest gets their own key. Driving on our site is not permitted during the night (10:00 pm to 7:00 am). For night owls and early birds, there is a 24h car park within the site.`,
          },
        ],
        sitePlan: true,
      },
      {
        id: "sanitaer",
        label: "Sanitary",
        heading: "Sanitary Facilities",
        summary: "Modern washrooms, showers, washing machine and dishwashing area.",
        highlights: ["Washrooms renovated 2024", "Showers & cubicles", "Washing machine & dryer"],
        image: "/images/IMG_7460-scaled.jpg",
        sections: [
          {
            title: "Sanitary",
            text: `There is a toilet house with a separate disposal room for chemical toilets, heated washrooms with cold and warm water at all 12 basins and eight showers. Additionally, two of the basins are separated by cubicles.

A room with four sinks is available for your dishes. You can also prepare small meals on a cooking facility. For your laundry there is a washing machine with dryer and laundry-sink.`,
          },
        ],
      },
      {
        id: "aufenthaltsraum",
        label: "Lounge",
        heading: "Relax with a view over the Main",
        summary: "Comfortable lounge with a wonderful view of the Main river – in any weather.",
        highlights: ["Main river view", "Cozy seating", "All-weather"],
        image: "/images/IMG_7603.jpg",
        sections: [
          {
            title: "Lounge",
            text: "Relax with a view over the Main river in our comfortable lounge area.",
          },
        ],
      },
      {
        id: "shop",
        label: "Shop",
        heading: "Camping Shop",
        summary: "Kiosk with essentials, drinks and freshly baked buns on pre-order.",
        highlights: ["Drinks & snacks", "Bun pre-order", "Supermarkets 2.5 km away"],
        image: "/images/IMG_7620-scaled.jpg",
        sections: [
          {
            title: "Shop",
            text: `You can buy some groceries, ice cream and cold drinks in our camping shop next to the reception for fair prices. From Monday to Saturday, there is also the possibility to order freshly baked buns the day before.

You can find several supermarkets, bakeries, butchers and more in a radius of 2.5 km around the campsite.`,
          },
        ],
      },
      {
        id: "dauercamping",
        label: "Seasonal Pitches",
        heading: "Seasonal Pitches",
        summary: "Parceled seasonal and winter storage pitches for caravans and motorhomes.",
        highlights: ["80–120 m² plots", "Winter storage", "Prices on request"],
        image: "/images/IMG_0888.jpg",
        sections: [
          {
            title: "Seasonal pitches per year",
            text: "Prices on request",
            list: {
              included: [
                "Approx. 80–120 m² parceled pitch for caravan with awning and car",
                "Use of sanitary facilities from April 1 – September 30",
                "2 persons",
                "Winter storage from October 1 – March 31",
              ],
              excluded: [
                "Electricity billed by consumption at €0.85/kWh, minimum €30 per season",
                "Waste disposal (€1.50 / bag)",
              ],
            },
          },
          {
            title: "Storage for caravans / motorhomes per year",
            text: "Prices on request",
          },
        ],
      },
    ],
  },
  experience: {
    eyebrow: "The site",
    title: "What guests appreciate here",
    subtitle: "Meadow, skyline, playground and the river Main – that is Mainkur.",
    items: [
      {
        key: "skyline",
        title: "Skyline views",
        description: "Frankfurt's skyline from the campsite – unique in the region.",
      },
      {
        key: "meadow",
        title: "Park-like grounds",
        description: "Old trees, open meadows and plenty of room to breathe.",
      },
      {
        key: "family",
        title: "For the whole family",
        description: "Relaxed camping with short walks to the playground and facilities.",
      },
      {
        key: "playground",
        title: "Play & fun",
        description: "Playground, table tennis and space for children to play.",
      },
      {
        key: "mainRiver",
        title: "Main river views",
        description: "Enjoy the river from the lounge – whatever the weather.",
      },
      {
        key: "evening",
        title: "Evening atmosphere",
        description: "Peace, nature and a place to unwind after a day of exploring.",
      },
    ],
  },
  imageBanners: {
    skyline: {
      quote: "City & nature",
      title: "Camping near Frankfurt – skyline on your doorstep",
      subtitle:
        "Overnight near Frankfurt while staying in nature: into the skyline in the morning, back to the meadow in the evening. The metropolis is just a short ride away – your quiet campsite near Frankfurt awaits.",
    },
    nature: {
      quote: "Calm & space",
      title: "Where time stands still",
      subtitle:
        "Open meadows, shady trees and the silence you miss in the city. That's what makes Mainkur so special.",
    },
  },
  surroundings: {
    eyebrow: "Around us",
    title: "Nearby",
    subtitle: "Frankfurt, shops and the boats on the Main are a short trip away.",
    items: [
      {
        key: "frankfurt",
        name: "Frankfurt am Main",
        description:
          "Ideal for overnight stays during Frankfurt trips: the metropolis with skyline, museums and old town is quickly reachable by bus, train or car.",
        distance: "approx. 15 km",
      },
      {
        key: "transport",
        name: "Public transportation",
        description:
          "Use trains, trams or buses to explore Frankfurt, Offenbach or Hanau. The next stop is only 1.1 km away.",
        distance: "1.1 km",
      },
      {
        key: "bakery",
        name: "Bakery & shopping",
        description:
          "The nearest bakery is open daily. LIDL and other shops are just minutes away.",
        distance: "1.4 km",
      },
      {
        key: "mainShip",
        name: "Primus Line",
        description:
          "The Primus Line is the biggest boat-cruising company in the state Hessen. They offer sightseeing trips and day cruises on the Main.",
        distance: "Fechenheim pier 2.0 km",
      },
    ],
  },
  directions: {
    title: "Directions",
    routes: [
      {
        via: "A661",
        steps: [
          'Take the exit Frankfurt Ost onto the street „Hanauer Landstraße" B8 in direction Hanau. Follow the street for approx. 3.2 km. After passing the bridge, turn right before the lights.',
        ],
      },
      {
        via: "A66",
        steps: [
          "Take the exit Maintal-Bischofsheim and turn right in direction Frankfurt.",
          "Turn right at the second light onto the road B8/40 (Frankfurter Landstraße).",
          "You will pass the campsite after 1.5 km.",
          'After another 800 m take the left lane and drive around the Restaurant „Bud & Spencer" and follow the signs in direction Maintal.',
          "After passing the bridge the second time, turn right before the lights.",
        ],
      },
    ],
  },
  openingHours: {
    title: "Opening hours",
    teaser: "Season from 1 April to 30 September. Arrive during reception hours; reserved pitches are ready from 11:00.",
    paragraphs: [
      "The reception is opened daily from 10 am to 12 am and 3 pm to 6 pm. Reserved pitches are available from 11 am.",
      "The pitch has to be left at departure day until 11 am.",
      "Our campsite is closed from October 1st to March 31st.",
      "Our campsite is opened seasonal from April 1st to September 30th.",
      "We would be pleased to welcome you soon!",
    ],
    signature: "Your Family Heike Borgstedt",
  },
  gallery: {
    eyebrow: "Photos",
    title: "Photos of the site",
    subtitle: "Pitches, sanitary facilities and the view across the Main.",
    featuredLabel: "Favourite moments",
    ctaText: "If a photo fits, book your pitch directly.",
    cta: "Book your pitch now",
  },
  contact: {
    eyebrow: "Contact",
    title: "How to reach us",
    teaser: "Heike and Frank Borgstedt are at reception during the season.",
    reachUs: "You can reach us daily from April 1 – September 30 between",
  },
  pricesPage: {
    title: "Prices",
    subtitle: "Price list 2026",
    partyHeading: "Travel party",
    pitchesHeading: "Pitches",
    pitchesHint:
      "Choose your travel party in any card – the selection applies to all categories at once.",
    additionalHeading: "Additional prices",
    breakdownLabel: "Per-night breakdown",
    adults: "Adults",
    children: "Children",
    dogs: "Dogs",
    electricity: "Electricity",
    totalPerNight: "Total per night",
    perNight: "/ night",
    cheapestBadge: "Best price",
    comparisonMoreThan: "{amount} / night more than {category}",
    quickSelectBack: "Quick select",
    cta: "Book online now",
    disclaimer: "Non-binding sample calculation per night. The final price is determined at booking.",
  },
  footer: {
    impressum: "Legal Notice",
    privacy: "Privacy Policy",
    agb: "Terms & Conditions",
    copyright: "Campingplatz Mainkur",
  },
  bookingHint: "For a booking, please use our online booking.",
};
